import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Brain } from 'threejs-brain-animation';
import './BrainBattery.css';

const RealisticBrainBattery = React.memo(({ metrics }) => {
  const [cognitiveLoad, setCognitiveLoad] = useState(0);
  const [activeAreas, setActiveAreas] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Calculate cognitive load from metrics
  useEffect(() => {
    if (!metrics || metrics.length === 0) return;

    const productivityMetrics = metrics.filter(m => 
      m.name.toLowerCase().includes('productivity') || 
      m.name.toLowerCase().includes('system') ||
      m.name.toLowerCase().includes('prefrontal')
    );
    
    const stressMetrics = metrics.filter(m => 
      m.name.toLowerCase().includes('stress') || 
      m.name.toLowerCase().includes('fatigue') ||
      m.name.toLowerCase().includes('cortisol')
    );

    const avgProductivity = productivityMetrics.length > 0
      ? productivityMetrics.reduce((sum, m) => sum + m.value, 0) / productivityMetrics.length
      : 50;

    const avgStress = stressMetrics.length > 0
      ? stressMetrics.reduce((sum, m) => sum + m.value, 0) / stressMetrics.length
      : 30;

    const load = Math.max(0, Math.min(100, avgProductivity - (avgStress * 0.5)));
    setCognitiveLoad(load);

    const areas = [];
    metrics.forEach(metric => {
      if (metric.value > 60) {
        areas.push({
          name: metric.name,
          intensity: metric.value,
        });
      }
    });
    setActiveAreas(areas);
  }, [metrics]);

  // Set loaded state after mount to prevent flash
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const getStatusText = useCallback(() => {
    if (cognitiveLoad > 75) return 'Optimal Performance';
    if (cognitiveLoad > 50) return 'Good Energy';
    if (cognitiveLoad > 25) return 'Low Battery';
    return 'Needs Recharge';
  }, [cognitiveLoad]);

  const getStatusColor = useCallback(() => {
    if (cognitiveLoad > 75) return '#10b981';
    if (cognitiveLoad > 50) return '#3b82f6';
    if (cognitiveLoad > 25) return '#f59e0b';
    return '#ef4444';
  }, [cognitiveLoad]);

  // Memoize brain style to prevent unnecessary re-renders
  const brainStyle = useMemo(() => ({
    width: '600px', 
    height: '500px',
    filter: `
      hue-rotate(${cognitiveLoad > 70 ? '140deg' : cognitiveLoad > 40 ? '220deg' : '340deg'})
      saturate(${cognitiveLoad > 70 ? '1.5' : cognitiveLoad > 40 ? '1.2' : '0.8'})
      brightness(${cognitiveLoad > 70 ? '1.3' : cognitiveLoad > 40 ? '1.1' : '0.9'})
      drop-shadow(0 0 20px ${getStatusColor()}80)
    `,
    transition: 'filter 1s ease'
  }), [cognitiveLoad, getStatusColor]);

  return (
    <div className="brain-battery-container">
      <div className="brain-battery-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h2 className="brain-battery-title">🧠 Brain Battery</h2>
          <button 
            className="info-button"
            onClick={() => setShowInfo(!showInfo)}
            title="Learn how Brain Battery works"
            aria-label="Information about Brain Battery"
          >
            ℹ️
          </button>
        </div>
        <div className="battery-status" style={{ color: getStatusColor() }}>
          <span className="status-text">{getStatusText()}</span>
          <span className="status-percentage">{Math.round(cognitiveLoad)}%</span>
        </div>
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div className="info-modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <button className="info-modal-close" onClick={() => setShowInfo(false)}>×</button>
            <h3>🧠 How Brain Battery Works</h3>
            
            <div className="info-section">
              <h4>📊 What It Measures</h4>
              <p>Your cognitive load is calculated from your real-time metrics:</p>
              <ul>
                <li><strong>Productivity Metrics:</strong> System 1, System 2, Prefrontal Cortex, Productivity Score</li>
                <li><strong>Stress Metrics:</strong> Stress Levels, Cognitive Fatigue, Cortisol Levels</li>
              </ul>
              <p style={{ fontSize: '0.9rem', marginTop: '15px', color: 'rgba(255,255,255,0.7)' }}>
                Higher productivity combined with lower stress results in a higher brain battery charge.
              </p>
            </div>

            <div className="info-section">
              <h4>🎨 Color Meanings</h4>
              <div className="color-guide">
                <div className="color-item">
                  <div className="color-dot" style={{ backgroundColor: '#10b981' }}></div>
                  <div>
                    <strong>Green (75-100%)</strong>
                    <p>Optimal Performance - Peak mental capacity</p>
                  </div>
                </div>
                <div className="color-item">
                  <div className="color-dot" style={{ backgroundColor: '#3b82f6' }}></div>
                  <div>
                    <strong>Blue (50-75%)</strong>
                    <p>Good Energy - Healthy cognitive state</p>
                  </div>
                </div>
                <div className="color-item">
                  <div className="color-dot" style={{ backgroundColor: '#f59e0b' }}></div>
                  <div>
                    <strong>Orange (25-50%)</strong>
                    <p>Low Battery - Consider taking a break</p>
                  </div>
                </div>
                <div className="color-item">
                  <div className="color-dot" style={{ backgroundColor: '#ef4444' }}></div>
                  <div>
                    <strong>Red (0-25%)</strong>
                    <p>Needs Recharge - Rest required</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h4>⚡ Active Brain Regions</h4>
              <p>Metrics with values above 60% are highlighted as "active areas" - showing which cognitive functions are currently engaged.</p>
            </div>

            <div className="info-section">
              <h4>💡 How to Use It</h4>
              <ul>
                <li><strong>Green Brain?</strong> Perfect time for deep work and complex tasks</li>
                <li><strong>Red/Orange Brain?</strong> Take a break, rest, or switch to lighter tasks</li>
                <li><strong>Monitor Trends:</strong> Track when you're most productive during the day</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      
      <div className="brain-battery-canvas-wrapper" style={{ height: '500px', position: 'relative' }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          position: 'relative',
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.5s ease-in'
        }}>
          <Brain style={brainStyle} />
          
          {/* Overlay effect based on cognitive load */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 50% 50%, ${getStatusColor()}30 0%, transparent 70%),
              linear-gradient(to top, ${getStatusColor()}15 0%, transparent 100%)
            `,
            pointerEvents: 'none',
            transition: 'background 1s ease'
          }} />
          
          {/* Pulsing glow effect */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${getStatusColor()}20 0%, transparent 70%)`,
            pointerEvents: 'none',
            animation: 'pulse 3s ease-in-out infinite',
            opacity: cognitiveLoad / 100
          }} />
        </div>

        <div className="battery-indicator">
          <div className="battery-shell">
            <div 
              className="battery-fill" 
              style={{ 
                height: `${cognitiveLoad}%`,
                backgroundColor: getStatusColor()
              }}
            />
          </div>
          <div className="battery-tip"></div>
        </div>
      </div>

      <div className="brain-battery-info">
        <div className="info-item">
          <span className="info-label">Active Areas</span>
          <span className="info-value">{activeAreas.length}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Cognitive Load</span>
          <span className="info-value">{Math.round(cognitiveLoad)}%</span>
        </div>
        <div className="info-item">
          <span className="info-label">Status</span>
          <span className="info-value" style={{ color: getStatusColor() }}>
            {cognitiveLoad > 50 ? '⚡ Charged' : '🔋 Charging'}
          </span>
        </div>
      </div>

      {activeAreas.length > 0 && (
        <div className="active-areas-list">
          <h4>Active Brain Regions</h4>
          <div className="areas-grid">
            {activeAreas.slice(0, 4).map((area, idx) => (
              <div key={idx} className="area-badge">
                <div 
                  className="area-indicator" 
                  style={{ backgroundColor: area.intensity > 70 ? '#10b981' : '#3b82f6' }}
                />
                <span className="area-name">{area.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="brain-controls">
        <p style={{ 
          color: 'rgba(255,255,255,0.7)', 
          fontSize: '0.85rem', 
          textAlign: 'center', 
          margin: '10px 0 0 0' 
        }}>
          🖱️ Interactive 3D Brain Model • Auto-rotating
        </p>
      </div>
    </div>
  );
});

export default RealisticBrainBattery;
